// ============================================================================
// CHANGO V5.0 — "app" SPA entry (Task #53)
// ============================================================================
// Auth flow + tenant workspace. Served at app.chango.local for all paths.
//
// Auth and tenant are intentionally bundled together: after /login the user
// transitions directly into /app/dashboard, so splitting them would force a
// second JS download at the most user-hostile moment (right after submitting
// credentials). Keeping them in one bundle lets the browser reuse the React
// runtime, AuthContext, and shared UI primitives.
//
// Routes:
//   /login /register /verify /forgot-password /reset-password /2fa
//   /403 /404
//   /dashboard                     (legacy post-login landing)
//   /app/dashboard                 (D3 app shell)
//     + /app/quotes, /app/quotes/new, /app/quotes/:id, /app/quotes/:id/edit,
//       /app/quotes/:id/print, /app/customers, /app/customers/new,
//       /app/customers/groups, /app/customers/:id,
//       /app/products, /app/products/new, /app/products/:id,
//       /app/settings, /app/billing/plans
//   /workspace/settings            (boss only, double-guard)
//
// Explicitly NOT in this bundle:
//   • Marketing pages (Landing, Pricing, SitePage)  — site bundle
//   • Capability URLs (/s/:token, /m/approval/:token) — public bundle
//   • Ops console (/ops/*)                           — ops bundle
//
// If a stray link points at /ops or / under app.chango.local, nginx hands
// the request back to this bundle and the catch-all Route renders NotFound.
// The operator gets a visible 404 rather than a silent route miss.
// ============================================================================

import React from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';

import '../styles/tailwind.css';

import { initTelemetry, captureError } from '../lib/telemetry.js';
initTelemetry().catch(() => { /* no-op */ });

if (typeof window !== 'undefined') {
  window.addEventListener('error', (ev) => {
    try { captureError(ev.error || new Error(ev.message || 'window.error'), { kind: 'window.error', spa: 'app' }); } catch {}
  });
  window.addEventListener('unhandledrejection', (ev) => {
    const reason = ev?.reason;
    try { captureError(reason instanceof Error ? reason : new Error(String(reason)), { kind: 'unhandledrejection', spa: 'app' }); } catch {}
  });
}

import { AuthProvider } from '../context/AuthContext.jsx';
import RequireAuth from '../components/RequireAuth.jsx';
import RoleGate from '../components/RoleGate.jsx';
import LayerGate from '../components/LayerGate.jsx';
import AppShell from '../components/AppShell.jsx';

import Login from '../pages/auth/Login.jsx';
import Register from '../pages/auth/Register.jsx';
import EmailVerify from '../pages/auth/EmailVerify.jsx';
import ForgotPassword from '../pages/auth/ForgotPassword.jsx';
import ResetPassword from '../pages/auth/ResetPassword.jsx';
import TwoFALogin from '../pages/auth/TwoFALogin.jsx';

import WorkspaceSettings from '../pages/workspace/Settings.jsx';

import AppDashboard from '../pages/app/AppDashboard.jsx';
import QuotesList from '../pages/app/QuotesList.jsx';
import QuoteWizard from '../pages/app/QuoteWizard.jsx';
import QuoteDetail from '../pages/app/QuoteDetail.jsx';
import QuotePrintable from '../pages/app/QuotePrintable.jsx';
import CustomersList from '../pages/app/CustomersList.jsx';
import CustomerNew from '../pages/app/CustomerNew.jsx';
import CustomerDetail from '../pages/app/CustomerDetail.jsx';
import CustomerGroupsManager from '../pages/app/CustomerGroupsManager.jsx';
import ProductsList from '../pages/app/ProductsList.jsx';
import ProductDetail from '../pages/app/ProductDetail.jsx';
import AppSettings from '../pages/app/AppSettings.jsx';
import BillingPlans from '../pages/app/BillingPlans.jsx';
import Welcome from '../pages/app/Welcome.jsx';
import ActivateTrial from '../pages/app/ActivateTrial.jsx';
import ResubmitLicense from '../pages/app/ResubmitLicense.jsx';

// Task #119 — Factory Management (Round 11 backend, Round 12 frontend wiring)
import MaterialCategoriesManager from '../pages/app/MaterialCategoriesManager.jsx';
import BomItemsLibrary from '../pages/app/BomItemsLibrary.jsx';
import BomItemDetail from '../pages/app/BomItemDetail.jsx';
import ProcessLibrary from '../pages/app/ProcessLibrary.jsx';
import ProductCostBreakdown from '../pages/app/ProductCostBreakdown.jsx';
import SensitivityAnalysis from '../pages/app/SensitivityAnalysis.jsx';
import CrossBorderChannel from '../pages/app/CrossBorderChannel.jsx';
import EntityProfitCenter from '../pages/app/EntityProfitCenter.jsx';

import Forbidden from '../pages/Forbidden.jsx';
import NotFound from '../pages/NotFound.jsx';

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <Routes>
          {/* Auth (no session) */}
          <Route path="/login"           element={<Login />} />
          <Route path="/register"        element={<Register />} />
          <Route path="/verify"          element={<EmailVerify />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password"  element={<ResetPassword />} />
          <Route path="/2fa"             element={<TwoFALogin />} />
          <Route path="/403"             element={<Forbidden />} />
          <Route path="/404"             element={<NotFound />} />

          {/* Unauth visitors landing at / get bounced to login — marketing
              lives on a different host. */}
          <Route path="/" element={<Navigate to="/login" replace />} />

          {/* Legacy post-login dashboard: never strand users outside AppShell. */}
          <Route
            path="/dashboard"
            element={
              <RequireAuth>
                <Navigate to="/app/dashboard" replace />
              </RequireAuth>
            }
          />

          {/* §9.6.2 Welcome / ActivateTrial / ResubmitLicense — 无 AppShell */}
          <Route
            path="/app/welcome"
            element={
              <RequireAuth>
                <Welcome />
              </RequireAuth>
            }
          />
          <Route
            path="/app/activate-trial"
            element={
              <RequireAuth>
                <ActivateTrial />
              </RequireAuth>
            }
          />
          <Route
            path="/app/resubmit-license"
            element={
              <RequireAuth>
                <ResubmitLicense />
              </RequireAuth>
            }
          />

          {/* D3 App shell — tenant-side, all authenticated users */}
          <Route
            path="/app"
            element={
              <RequireAuth>
                <AppShell />
              </RequireAuth>
            }
          >
            <Route index element={<Navigate to="/app/dashboard" replace />} />
            <Route path="dashboard"         element={<AppDashboard />} />
            <Route path="quotes"            element={<QuotesList />} />
            <Route path="quotes/new"        element={<QuoteWizard />} />
            <Route path="quotes/:id"        element={<QuoteDetail />} />
            <Route path="quotes/:id/edit"   element={<QuoteWizard />} />
            <Route path="quotes/:id/print"  element={<QuotePrintable />} />
            <Route path="customers"         element={<CustomersList />} />
            <Route path="customers/new"     element={<CustomerNew />} />
            <Route path="customers/groups"  element={<CustomerGroupsManager />} />
            <Route path="customers/:id"     element={<CustomerDetail />} />
            <Route path="products"          element={<ProductsList />} />
            <Route path="products/new"      element={<ProductDetail />} />
            <Route path="products/:id"      element={<ProductDetail />} />
            <Route path="settings"          element={<AppSettings />} />
            <Route
              path="billing/plans"
              element={
                <RoleGate allow={['boss', 'finance']} fallback={<Navigate to="/403" replace />}>
                  <BillingPlans />
                </RoleGate>
              }
            />
            {/* Task #119 — Factory Management (boss/finance only) */}
            <Route
              path="factory/material-categories"
              element={
                <RoleGate allow={['boss', 'finance']} fallback={<Navigate to="/403" replace />}>
                  <LayerGate layer="A" fallback={<Navigate to="/403" replace />}>
                    <MaterialCategoriesManager />
                  </LayerGate>
                </RoleGate>
              }
            />
            <Route
              path="factory/bom-items"
              element={
                <RoleGate allow={['boss', 'finance']} fallback={<Navigate to="/403" replace />}>
                  <LayerGate layer="A" fallback={<Navigate to="/403" replace />}>
                    <BomItemsLibrary />
                  </LayerGate>
                </RoleGate>
              }
            />
            <Route
              path="factory/bom-items/:code"
              element={
                <RoleGate allow={['boss', 'finance']} fallback={<Navigate to="/403" replace />}>
                  <LayerGate layer="A" fallback={<Navigate to="/403" replace />}>
                    <BomItemDetail />
                  </LayerGate>
                </RoleGate>
              }
            />
            <Route
              path="factory/processes"
              element={
                <RoleGate allow={['boss', 'finance']} fallback={<Navigate to="/403" replace />}>
                  <LayerGate layer="A" fallback={<Navigate to="/403" replace />}>
                    <ProcessLibrary />
                  </LayerGate>
                </RoleGate>
              }
            />
            <Route
              path="products/:code/cost-breakdown"
              element={
                <RoleGate allow={['boss', 'finance']} fallback={<Navigate to="/403" replace />}>
                  <LayerGate layer="A" fallback={<Navigate to="/403" replace />}>
                    <ProductCostBreakdown />
                  </LayerGate>
                </RoleGate>
              }
            />
            <Route
              path="products/:code/sensitivity"
              element={
                <RoleGate allow={['boss', 'finance']} fallback={<Navigate to="/403" replace />}>
                  <LayerGate layer="A" fallback={<Navigate to="/403" replace />}>
                    <SensitivityAnalysis />
                  </LayerGate>
                </RoleGate>
              }
            />
            <Route
              path="crossborder"
              element={
                <RoleGate allow={['boss', 'finance']} fallback={<Navigate to="/403" replace />}>
                  <LayerGate layer="C" fallback={<Navigate to="/403" replace />}>
                    <CrossBorderChannel />
                  </LayerGate>
                </RoleGate>
              }
            />
            <Route
              path="entities"
              element={
                <RoleGate allow={['boss', 'finance']} fallback={<Navigate to="/403" replace />}>
                  <LayerGate layer="E" fallback={<Navigate to="/403" replace />}>
                    <EntityProfitCenter />
                  </LayerGate>
                </RoleGate>
              }
            />
          </Route>

          {/* Workspace admin — boss only, double-guard */}
          <Route
            path="/workspace/settings"
            element={
              <RequireAuth>
                <RoleGate allow={['boss']} fallback={<Navigate to="/403" replace />}>
                  <WorkspaceSettings />
                </RoleGate>
              </RequireAuth>
            }
          />

          <Route path="*" element={<NotFound />} />
        </Routes>
      </AuthProvider>
    </BrowserRouter>
  );
}

const root = createRoot(document.getElementById('root'));
root.render(
  <React.StrictMode>
    <App />
  </React.StrictMode>
);
